import React, {useEffect, useState, useRef, useMemo} from "react";
import ChartViewer from "../../pages/Campaign/ReportingComponents/ChartViewer";
import {useCubeQuery} from "../../utils/hooks/useCubeQuery";
import {Select} from "antd";
import DateSelect from "../buttons/DateSelect";
import useCubeClient from "../../utils/cubejs-client";
import dayjs from "dayjs";
import {useCurrentUser} from "../../utils/CurrentUserContext";

const HomeGraph = () => {
  const [isTransitioning, setIsTransitioning] = useState(false);
  const [metric, setMetric] = useState("tdImpressions");
  const [dateRange, setDateRange] = useState([
    dayjs().subtract(1, "month").startOf("day"),
    dayjs().endOf("day"),
  ]); // Initial default range
  const [clicksData, setClicksData] = useState(null);
  const [impressionsData, setImpressionsData] = useState(null);
  const graphContainerRef = useRef(null);
  const [containerHeight, setContainerHeight] = useState(200);
  const cubejsApi = useCubeClient();
  const {user} = useCurrentUser();

  const options = [
    {label: "Reads", value: "reads"},
    {label: "Impressions", value: "tdImpressions"},
    {label: "Clickouts", value: "clickouts"},
    {label: "Conversions", value: "conversions"},
    {label: "Clicks", value: "tdClicks"},
    {label: "Reads per 100 Imps", value: "readImps"},
    {label: "Average Read Time", value: "average_readtime"},
    {label: "CTR", value: "ctr"},
    ...(user?.company?.type === "PUBLISHER"
      ? [
        {label: "eCPM", value: "eCpm"},
        {label: "Total Revenue", value: "publisherRevenue"},
      ]
      : [{label: "Total Cost", value: "totalCost"}]),
  ];


  const getMetricLabel = (metric) => {
    const selectedOption = options.find(option => option.value === metric);
    return selectedOption ? selectedOption.label : metric;
  };

  const {resultSet: aggData, error: aggError, isLoading: aggIsLoading} = useCubeQuery(
    {
      measures: metric !== "ctr" ? [`aggregateTdLevel.${metric}`] : [],
      order: {
        "aggregateTdLevel.date": "asc",
      },
      filters: [
        {
          member: "aggregateTdLevel.fraud",
          operator: "notEquals",
          values: ["true"],
        },
      ],
      timeDimensions: [
        {
          dimension: "aggregateTdLevel.date",
          granularity: "day",
          dateRange: dateRange.map((date) => date.toISOString().split("T")[0]),
        },
      ],
    },
    {cubejsApi}
  );

  // Additional query for CTR metric to get both clicks and impressions
  const {resultSet: ctrData, error: ctrError, isLoading: ctrIsLoading} = useCubeQuery(
    {
      measures: ["aggregateTdLevel.tdClicks", "aggregateTdLevel.tdImpressions"],
      order: {
        "aggregateTdLevel.date": "asc",
      },
      filters: [
        {
          member: "aggregateTdLevel.fraud",
          operator: "notEquals",
          values: ["true"],
        },
      ],
      timeDimensions: [
        {
          dimension: "aggregateTdLevel.date",
          granularity: "day",
          dateRange: dateRange.map((date) => date.toISOString().split("T")[0]),
        },
      ],
    },
    {cubejsApi}
  );

  useEffect(() => {
    setIsTransitioning(true);
    const timer = setTimeout(() => {
      setIsTransitioning(false);
    }, 300); // Delay for smooth transition
    return () => clearTimeout(timer);
  }, [metric, dateRange]);

  const handleDateChange = (range) => {
    setDateRange(range);
  };
  
  const processedCtrData = useMemo(() => {
    if (!ctrData) return null;
    
    try {
      return ctrData.tablePivot().map(row => {
        const clicks = row['aggregateTdLevel.tdClicks'] || 0;
        const impressions = row['aggregateTdLevel.tdImpressions'] || 0;
        // Calculate CTR as percentage (clicks / impressions * 100)
        const ctr = impressions > 0 ? (clicks / impressions) * 100 : 0;
        
        return {
          date: row['aggregateTdLevel.date.day'],
          ctr: parseFloat(ctr.toFixed(2)) // Round to 2 decimal places
        };
      });
    } catch (error) {
      return null;
    }
  }, [ctrData]);

  if (aggError || (metric === "ctr" && ctrError)) {
    return <div>Error: {(aggError || ctrError).toString()}</div>;
  }

  const resultSet = {
    chartPivot: (pivotConfig) => {
      if (metric === 'ctr' && processedCtrData) {
        return processedCtrData.map(row => ({
          x: row.date,
          y: row.ctr
        }));
      }
      
      return aggData?.tablePivot(pivotConfig).map((row) => ({
        x: row["aggregateTdLevel.date.day"],
        y: row[`aggregateTdLevel.${metric}`],
      })) || [];
    },
    series: (pivotConfig) => [
      {
        title: getMetricLabel(metric),
        series: metric === 'ctr' && processedCtrData
          ? processedCtrData.map(row => ({
              value: row.ctr
            }))
          : aggData?.tablePivot(pivotConfig).map((row) => ({
              value: row[`aggregateTdLevel.${metric}`],
            })) || [],
      },
    ],
  };

  const pivotConfig = {
    x: ["aggregateTdLevel.date.day"],
    y: [`aggregateTdLevel.${metric}`],
  };

  return (
    <div className="mb-4">
      <div
        ref={graphContainerRef}
        className="mt-4 grid grid-cols-1 divide-y divide-gray-200 overflow-hidden rounded-lg bg-white shadow p-4 md:grid-cols-6 md:divide-y-0 md:divide-x"
      >
        <div className="col-span-full flex flex-wrap items-center justify-between mb-4">
          <div className="flex flex-wrap items-center justify-between">
            <div
              className={`sm:grid sm:gap-4 sm:items-start py-3 pt-0 sm:border-gray-200`}
            >
              <Select
                className="max-w-lg w-[400px] shadow-sm sm:max-w-xs h-[38px] border-gray-300 focus:ring-indigo-500 focus:border-indigo-500 rounded-md mr-2"
                defaultValue="reads"
                value={metric}
                onChange={setMetric}
                options={options}
              />
            </div>
            <DateSelect
              dateRange={dateRange}
              handleDate={handleDateChange}
            />
          </div>
        </div>
        <h2 className="col-span-full text-xl font-bold mb-4 text-gray-700 first-letter:uppercase">
          {`${getMetricLabel(metric)} Over Time`}
        </h2>
        <div className="col-span-full">
          {aggIsLoading || (metric === 'ctr' && ctrIsLoading) || isTransitioning ? (
            <div
              style={{minHeight: `300px`}}
              className="flex justify-center items-center text-gray-500"
            >
              Loading graph...
            </div>
          ) : (
            <ChartViewer resultSet={resultSet} pivotConfig={pivotConfig} chartType="line" height={"300px"}/>
          )}
        </div>
      </div>
    </div>
  );
};

export default HomeGraph;
